import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, BookOpen, Compass, Globe2 } from "lucide-react";
import { useVerticalsList } from "@/lib/verticalsApi";
import { COUNTRY_HUBS } from "@/lib/countryHubs";

/**
 * BlogDiscovery — unified navigation for the Blog listing.
 *
 * Consolidates the previous "Explore Industries" pills + "Complete
 * Guides" row + Categories into one premium tabbed component.
 *
 * Rules:
 *   • Renders ONLY on the unfiltered first page — filtered / paginated
 *     views should not carry heavy navigation.
 *   • Tabs share a single design language: uppercase eyebrow labels,
 *     consistent spacing, subtle underline on the active tab.
 *   • Content pane keeps a fixed comfortable min-height so the article
 *     grid position is stable when the tab changes.
 *   • Nothing routes through JavaScript — every tile is a real `<Link>`
 *     so SEO crawlers still see the entries.
 */
export default function BlogDiscovery() {
  const [tab, setTab] = useState("guides");
  const [guides, setGuides] = useState([]);
  const [guidesLoaded, setGuidesLoaded] = useState(false);
  const { data: vertData } = useVerticalsList();
  const verticals = (vertData?.docs || []).filter((v) => (v.articleCount ?? 0) > 0);

  useEffect(() => {
    let cancelled = false;
    fetch("/api/guides?limit=12&depth=1&sort=createdAt")
      .then((r) => (r.ok ? r.json() : { docs: [] }))
      .then((body) => {
        if (cancelled) return;
        const docs = (body?.docs || []).filter(
          (g) => g?.slug && g?.service && typeof g.service === "object" && g.service.slug,
        );
        setGuides(docs);
        setGuidesLoaded(true);
      })
      .catch(() => { if (!cancelled) setGuidesLoaded(true); });
    return () => { cancelled = true; };
  }, []);

  // Hide the whole block if we have nothing to show yet.
  if (guides.length === 0 && verticals.length === 0) return null;

  const tabs = [
    { id: "guides", label: "Complete Guides", count: guides.length, icon: BookOpen },
    { id: "countries", label: "Country Hubs", count: COUNTRY_HUBS.length, icon: Globe2 },
    { id: "industries", label: "Industries", count: verticals.length, icon: Compass },
  ];

  return (
    <section
      className="mb-10 rounded-2xl border border-neutral-200 bg-white overflow-hidden"
      aria-label="Blog discovery"
      data-testid="blog-discovery"
    >
      {/* Tab bar */}
      <div className="flex flex-wrap items-center justify-between gap-2 px-5 md:px-6 py-3 border-b border-neutral-200 bg-[#fafafa]">
        <div role="tablist" aria-label="Browse the blog" className="flex flex-wrap items-center gap-1">
          {tabs.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                role="tab"
                aria-selected={active}
                aria-controls={`discovery-panel-${t.id}`}
                onClick={() => setTab(t.id)}
                data-testid={`blog-discovery-tab-${t.id}`}
                className={
                  "inline-flex items-center gap-2 text-[12px] font-medium tracking-tight " +
                  "px-3 py-1.5 rounded-full transition-colors " +
                  (active
                    ? "bg-neutral-900 text-white"
                    : "text-neutral-600 hover:text-neutral-900")
                }
              >
                <Icon size={13} strokeWidth={1.75} />
                {t.label}
                <span className={active ? "text-white/70" : "text-neutral-400"}>
                  {t.count}
                </span>
              </button>
            );
          })}
        </div>
        <Link
          to={
            tab === "guides"
              ? "/services"
              : tab === "countries"
              ? "/services"
              : "/verticals"
          }
          className="hidden sm:inline-flex items-center gap-1 text-[12px] font-medium text-neutral-600 hover:text-neutral-900"
          data-testid="blog-discovery-see-all"
        >
          {tab === "guides"
            ? "See all services"
            : tab === "countries"
            ? "See all services"
            : "All industries"}
          <ArrowRight size={12} />
        </Link>
      </div>

      {/* Panel */}
      <div className="px-5 md:px-6 py-5 min-h-[172px]">
        {tab === "guides" && (
          <div
            id="discovery-panel-guides"
            role="tabpanel"
            aria-labelledby="blog-discovery-tab-guides"
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5"
            data-testid="blog-discovery-panel-guides"
          >
            {guides.map((g) => (
              <Link
                key={g.id}
                to={`/guides/${g.slug}`}
                data-testid={`blog-discovery-guide-${g.service.slug}`}
                className="group flex flex-col justify-between rounded-lg border border-neutral-200 px-3 py-2.5 hover:border-neutral-900 transition-colors bg-white"
              >
                <div className="text-[13px] font-medium text-neutral-900 leading-snug line-clamp-2">
                  {g.service.title}
                </div>
                <span className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-neutral-500 group-hover:text-neutral-900">
                  Read guide <ArrowRight size={10} />
                </span>
              </Link>
            ))}
            {!guidesLoaded && guides.length === 0 && (
              <p className="text-[13px] text-neutral-500 col-span-full">Loading…</p>
            )}
          </div>
        )}
        {tab === "countries" && (
          <div
            id="discovery-panel-countries"
            role="tabpanel"
            aria-labelledby="blog-discovery-tab-countries"
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5"
            data-testid="blog-discovery-panel-countries"
          >
            {COUNTRY_HUBS.map((c) => (
              <Link
                key={c.code}
                to={`/blog/${c.hubSlug}`}
                data-testid={`blog-discovery-country-${c.code}`}
                className="group flex flex-col justify-between rounded-lg border border-neutral-200 px-3 py-2.5 hover:border-neutral-900 transition-colors bg-white"
              >
                <div className="text-[13px] font-medium text-neutral-900 leading-snug">
                  Sourcing from {c.label}
                </div>
                <span className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-neutral-500 group-hover:text-neutral-900">
                  Explore hub <ArrowUpRight size={10} />
                </span>
              </Link>
            ))}
          </div>
        )}
        {tab === "industries" && (
          <div
            id="discovery-panel-industries"
            role="tabpanel"
            aria-labelledby="blog-discovery-tab-industries"
            className="flex flex-wrap gap-1.5"
            data-testid="blog-discovery-panel-industries"
          >
            {verticals.map((v) => (
              <Link
                key={v.slug}
                to={`/verticals/${v.slug}`}
                data-testid={`blog-discovery-industry-${v.slug}`}
                className="inline-flex items-center gap-2 rounded-full border border-neutral-200 px-3.5 py-1.5 text-[12px] font-medium text-neutral-800 hover:border-neutral-900 hover:text-neutral-900 transition-colors bg-white"
              >
                {v.name}
                <span className="text-[11px] text-neutral-400 tabular-nums">
                  {v.articleCount ?? 0}
                </span>
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
